import { useTheme } from "@emotion/react";
import { tokens } from "../../theme";
import { useState, useEffect, useMemo } from "react";

import {
    Box,
    Typography,
    LinearProgress,
    Paper,
    Switch,
    Skeleton,
    Tooltip,
    Divider,
    Chip,
} from "@mui/material";

import { PieChart } from "react-minimal-pie-chart";
import { djangoApi } from "../../utils/axios/axios.utils";

const RESUME_SCOPE_CACHE_KEY = "pitaya_resume_info_scope";
const RESUME_PERCENT_BASE_CACHE_KEY = "pitaya_resume_info_percent_base";

const getCachedBoolean = (key, fallback) => {
    try {
        const value = localStorage.getItem(key);

        if (value === null) return fallback;

        return value === "true";
    } catch (error) {
        return fallback;
    }
};

const ResumeInfoBox = ({ sellerInfo, avgKitValues }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [isLoadingInfo, setIsLoadingInfo] = useState(false);
    const [dataUser, setDataUser] = useState(null);

    const [showingAlt, setShowingAlt] = useState(() => {
        return getCachedBoolean(RESUME_SCOPE_CACHE_KEY, true);
    });

    const [showPercentBase, setShowPercentBase] = useState(() => {
        return getCachedBoolean(RESUME_PERCENT_BASE_CACHE_KEY, false);
    });

    const chartPalette = [
        "#C79A5B",
        "#D6D6D6",
        "#B76E79",
        "#9D7A56",
        "#6F5E53",
        "#E6C16A",
        "#A88A67",
        "#F2D7C3",
        "#8B7355",
        "#C0A080",
    ];

    const formatCurrency = (value) => {
        return new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
        }).format(Number(value || 0));
    };

    const formatNumber = (value) => {
        return new Intl.NumberFormat("pt-BR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(Number(value || 0));
    };

    const capitalizeFirstLetter = (string) => {
        if (!string) return "";
        return string.charAt(0).toUpperCase() + string.slice(1);
    };

    const getSoldQuantity = (product) => {
        const quantity = Number(product?.quantity || 0);
        const returned = Number(product?.quantity_returned || 0);

        return Math.max(quantity - returned, 0);
    };

    const getColor = (percentage) => {
        if (percentage <= 30) return "#f44336";
        if (percentage <= 75) return "#ff9800";
        return "#4caf50";
    };

    const handleChangeScope = (value) => {
        setShowingAlt(value);

        try {
            localStorage.setItem(RESUME_SCOPE_CACHE_KEY, String(value));
        } catch (error) {
            // localStorage indisponível, ignora sem quebrar a tela
        }
    };

    const handleChangePercentBase = (value) => {
        setShowPercentBase(value);

        try {
            localStorage.setItem(RESUME_PERCENT_BASE_CACHE_KEY, String(value));
        } catch (error) {
            // localStorage indisponível, ignora sem quebrar a tela
        }
    };

    useEffect(() => {
        const getSellerSelsInfo = async () => {
            setIsLoadingInfo(true);

            try {
                const res = await djangoApi.get(
                    `products/register_prods/${sellerInfo.id}/get_kitprods_data_seller_info/`,
                    {
                        headers: {
                            Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                const { data, status } = res;

                if (status === 200) {
                    console.log("seller resume info:", data);
                    setDataUser(data.data);
                }
            } catch (err) {
                console.log("Erro ao buscar informações da revendedora", err);
                window.alert(err);
            } finally {
                setIsLoadingInfo(false);
            }
        };

        if (sellerInfo?.id) {
            getSellerSelsInfo();
        }
    }, [sellerInfo?.id]);

    const lastKits = dataUser?.last_3_kits || [];
    const totalKits = dataUser?.kits_ids || [];
    const period = dataUser?.period;

    const filterKits = useMemo(() => {
        return showingAlt ? totalKits : lastKits;
    }, [showingAlt, totalKits, lastKits]);

    const modelProducts = useMemo(() => {
        const arr = dataUser?.data_prods || [];

        return arr.filter((item) => filterKits.includes(item.kit_id));
    }, [dataUser, filterKits]);

    const ringProducts = useMemo(() => {
        const arr = dataUser?.aneis_data_prods || [];

        return arr.filter((item) => filterKits.includes(item.kit_id));
    }, [dataUser, filterKits]);

    const colorProducts = useMemo(() => {
        const arr = dataUser?.color_data_prods || [];

        return arr.filter((item) => filterKits.includes(item.kit_id));
    }, [dataUser, filterKits]);

    const rawProducts = useMemo(() => {
        const arr = dataUser?.raw_data_prods || [];

        return arr.filter((item) => filterKits.includes(item.kit_id));
    }, [dataUser, filterKits]);

    const totalSoldProds = useMemo(() => {
        return modelProducts.reduce((acc, item) => acc + getSoldQuantity(item), 0);
    }, [modelProducts]);

    const modelSalesData = useMemo(() => {
        const consolidated = modelProducts
            .sort((a, b) => String(a.model).localeCompare(String(b.model)))
            .reduce((acc, item) => {
                const model = item.model || "outros";

                if (!acc[model]) {
                    acc[model] = {
                        model,
                        sold: 0,
                        total: 0,
                        sellPrice: 0,
                    };
                }

                acc[model].sold += getSoldQuantity(item);
                acc[model].total += Number(item?.quantity || 0);
                acc[model].sellPrice += Number(item?.sell_price || 0);

                return acc;
            }, {});

        return Object.values(consolidated);
    }, [modelProducts]);

    const totalRingsSold = useMemo(() => {
        return ringProducts.reduce((acc, item) => acc + getSoldQuantity(item), 0);
    }, [ringProducts]);

    const ringSalesData = useMemo(() => {
        const consolidated = ringProducts.reduce((acc, item) => {
            const size = `${item?.tamanho || "-"}`;

            if (!acc[size]) {
                acc[size] = {
                    size,
                    sold: 0,
                    total: 0,
                };
            }

            acc[size].sold += getSoldQuantity(item);
            acc[size].total += Number(item?.quantity || 0);

            return acc;
        }, {});

        return Object.values(consolidated)
            .filter((item) => item.sold > 0)
            .sort((a, b) => b.sold - a.sold);
    }, [ringProducts]);

    const colorBathSalesData = useMemo(() => {
        const consolidated = colorProducts.reduce((acc, item) => {
            const colorName = item?.color || "Sem cor";

            if (!acc[colorName]) {
                acc[colorName] = {
                    title: colorName,
                    value: 0,
                    soldValue: 0,
                    avgTicket: 0,
                };
            }

            acc[colorName].value += getSoldQuantity(item);
            acc[colorName].soldValue += Number(item?.sell_price || 0);

            return acc;
        }, {});

        const sorted = Object.values(consolidated)
            .filter((item) => item.value > 0)
            .sort((a, b) => b.value - a.value);

        return sorted.map((item, index) => ({
            ...item,
            color: chartPalette[index % chartPalette.length],
            avgTicket: item.value > 0 ? item.soldValue / item.value : 0,
        }));
    }, [colorProducts]);

    const totalColorBathSoldUnits = useMemo(() => {
        return colorBathSalesData.reduce((acc, item) => acc + item.value, 0);
    }, [colorBathSalesData]);

    const totalColorBathSoldValue = useMemo(() => {
        return colorBathSalesData.reduce((acc, item) => acc + item.soldValue, 0);
    }, [colorBathSalesData]);

    const averageSoldProductTicket = useMemo(() => {
        return totalColorBathSoldUnits > 0
            ? totalColorBathSoldValue / totalColorBathSoldUnits
            : 0;
    }, [totalColorBathSoldUnits, totalColorBathSoldValue]);

    const averageProductsInsideKitTicket = useMemo(() => {
        const totalUnitsInsideKits = rawProducts.reduce((acc, item) => {
            return acc + Number(item?.quantity || 0);
        }, 0);

        const totalValueInsideKits = rawProducts.reduce((acc, item) => {
            const quantity = Number(item?.quantity || 0);
            const sellPrice = Number(item?.sell_price || 0);

            return acc + quantity * sellPrice;
        }, 0);

        return totalUnitsInsideKits > 0
            ? totalValueInsideKits / totalUnitsInsideKits
            : 0;
    }, [rawProducts]);

    const totalProductsInsideKits = useMemo(() => {
        return rawProducts.reduce((acc, item) => {
            return acc + Number(item?.quantity || 0);
        }, 0);
    }, [rawProducts]);

    const colorPerformanceData = useMemo(() => {
        const consolidated = rawProducts.reduce((acc, item) => {
            const colorName = item?.color || "Sem cor";

            const quantitySent = Number(item?.quantity || 0);
            const quantitySold = getSoldQuantity(item);
            const sellPrice = Number(item?.sell_price || 0);
            const soldValue = quantitySold * sellPrice;

            if (!acc[colorName]) {
                acc[colorName] = {
                    color: colorName,
                    quantitySent: 0,
                    quantitySold: 0,
                    soldValue: 0,
                };
            }

            acc[colorName].quantitySent += quantitySent;
            acc[colorName].quantitySold += quantitySold;
            acc[colorName].soldValue += soldValue;

            return acc;
        }, {});

        const result = Object.values(consolidated);

        const totalSent = result.reduce((acc, item) => acc + item.quantitySent, 0);
        const totalSold = result.reduce((acc, item) => acc + item.quantitySold, 0);

        const sorted = result
            .map((item) => {
                const sentPercentage = totalSent > 0
                    ? Math.round((item.quantitySent / totalSent) * 100)
                    : 0;

                const soldPercentage = totalSold > 0
                    ? Math.round((item.quantitySold / totalSold) * 100)
                    : 0;

                const conversionPercentage = item.quantitySent > 0
                    ? Math.round((item.quantitySold / item.quantitySent) * 100)
                    : 0;

                return {
                    ...item,
                    sentPercentage,
                    soldPercentage,
                    conversionPercentage,
                    averageSoldTicket: item.quantitySold > 0
                        ? item.soldValue / item.quantitySold
                        : 0,
                };
            })
            .filter((item) => item.quantitySent > 0)
            .sort((a, b) => b.quantitySold - a.quantitySold);

        return sorted.map((item, index) => ({
            ...item,
            chartColor: chartPalette[index % chartPalette.length],
        }));
    }, [rawProducts]);

    const scopeLabel = showingAlt
        ? `Base: últimos ${period?.months || 12} meses`
        : lastKits.length === 1
            ? "Base: última maleta"
            : `Base: últimas ${lastKits.length || 0} maletas`;

    if (isLoadingInfo) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
                <Paper elevation={6} sx={{ backgroundColor: colors.pink[800], padding: 2, minHeight: 260 }}>
                    <Skeleton variant="text" width={240} height={28} sx={{ mb: 2 }} />
                    <Box sx={{ display: "flex", gap: 2 }}>
                        {Array(7).fill(null).map((_, index) => (
                            <Box key={index} sx={{ backgroundColor: "#ffffff11", borderRadius: 2, padding: 2, flex: 1 }}>
                                <Skeleton variant="text" width="60%" height={20} sx={{ mx: "auto", mb: 1 }} />
                                <Skeleton variant="circular" width={60} height={60} sx={{ mx: "auto" }} />
                                <Skeleton variant="text" width="50%" height={20} sx={{ mx: "auto", mt: 2 }} />
                            </Box>
                        ))}
                    </Box>
                </Paper>

                <Paper elevation={6} sx={{ backgroundColor: colors.pink[800], padding: 2, minHeight: 180 }}>
                    <Skeleton variant="text" width={220} height={28} sx={{ mb: 2 }} />
                    <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                </Paper>
            </Box>
        );
    }

    if (!isLoadingInfo && lastKits.length === 0) {
        return (
            <Paper
                elevation={6}
                sx={{
                    backgroundColor: colors.pink[800],
                    padding: 2,
                    minHeight: 120,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Typography variant="h5">SEM DADOS POR ENQUANTO</Typography>
            </Paper>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "20px",
                    width: "100%",
                    minHeight: "260px",
                    alignItems: "stretch",
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Paper elevation={6} sx={{ backgroundColor: colors.pink[800], padding: 2, height: "100%" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 3, flexWrap: "wrap", mb: 2 }}>
                            <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: !showingAlt ? "bold" : "normal", cursor: "pointer" }}
                                        onClick={() => handleChangeScope(false)}
                                    >
                                        {lastKits.length === 1 ? "última Maleta" : `últimas ${lastKits.length || 0} Maletas`}
                                    </Typography>

                                    <Switch
                                        checked={showingAlt}
                                        onChange={(event) => handleChangeScope(event.target.checked)}
                                        color="success"
                                    />

                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: showingAlt ? "bold" : "normal", cursor: "pointer" }}
                                        onClick={() => handleChangeScope(true)}
                                    >
                                        Últimos 12 meses
                                    </Typography>
                                </Box>

                                <Divider orientation="vertical" flexItem />

                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Tooltip title="Calcula o percentual de vendas sobre o total de vendas.">
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: !showPercentBase ? "bold" : "normal", cursor: "pointer" }}
                                            onClick={() => handleChangePercentBase(false)}
                                        >
                                            Vendas
                                        </Typography>
                                    </Tooltip>

                                    <Switch
                                        checked={showPercentBase}
                                        onChange={(event) => handleChangePercentBase(event.target.checked)}
                                        color="success"
                                    />

                                    <Tooltip title="Calcula o percentual de vendas sobre o total de produtos enviados nas maletas.">
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: showPercentBase ? "bold" : "normal", cursor: "pointer" }}
                                            onClick={() => handleChangePercentBase(true)}
                                        >
                                            Maletas
                                        </Typography>
                                    </Tooltip>
                                </Box>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                                <Chip
                                    size="small"
                                    label={scopeLabel}
                                    sx={{ color: "#fff", backgroundColor: "#ffffff18" }}
                                />
                                <Typography fontWeight="bold">
                                    Média das Maletas: {avgKitValues}
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                gap: 2,
                                justifyContent: "space-between",
                                overflowX: "auto",
                                pb: 0.5,
                                "&::-webkit-scrollbar": { height: "6px" },
                                "&::-webkit-scrollbar-thumb": {
                                    backgroundColor: colors.pink[500],
                                    borderRadius: "4px",
                                },
                                "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                            }}
                        >
                            {modelSalesData.map((item, index) => {
                                let percentage = 0;

                                if (!showPercentBase) {
                                    percentage = totalSoldProds > 0
                                        ? Math.round((item.sold / totalSoldProds) * 100)
                                        : 0;
                                } else {
                                    percentage = item.total > 0
                                        ? Math.round((item.sold / item.total) * 100)
                                        : 0;
                                }

                                return (
                                    <Box
                                        key={`${item.model}-${index}`}
                                        sx={{
                                            backgroundColor: "#ffffff11",
                                            borderRadius: 2,
                                            padding: 2,
                                            minWidth: 145,
                                            flex: 1,
                                            textAlign: "center",
                                        }}
                                    >
                                        <Typography fontWeight="bold" mb={1}>
                                            {capitalizeFirstLetter(item.model)}
                                        </Typography>

                                        <PieChart
                                            data={[
                                                { value: percentage, color: getColor(percentage) },
                                                { value: Math.max(100 - percentage, 0), color: "#ddd3" },
                                            ]}
                                            totalValue={100}
                                            lineWidth={20}
                                            rounded
                                            style={{ height: "80px" }}
                                            label={() => `${percentage}%`}
                                            labelStyle={{
                                                fontSize: "20px",
                                                fill: "#fff",
                                                fontWeight: "bold",
                                            }}
                                            labelPosition={0}
                                            animate
                                        />

                                        <Box sx={{ display: "flex", justifyContent: "space-around", alignItems: "center", gap: 1 }}>
                                            <Typography variant="h5" mt={1}>
                                                {showPercentBase
                                                    ? `${item.sold}/${item.total}`
                                                    : `${item.sold}/${totalSoldProds}`}
                                            </Typography>

                                            <Tooltip title="Preço médio dos produtos vendidos desta categoria.">
                                                <Typography variant="h5" mt={1}>
                                                    {formatCurrency(item.sold === 0 ? 0 : item.sellPrice / item.sold)}
                                                </Typography>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                </Box>

                <Box sx={{ width: "300px", flexShrink: 0 }}>
                    <Paper elevation={6} sx={{ backgroundColor: colors.pink[800], padding: 2, height: "100%" }}>
                        <Typography sx={{ fontWeight: "bold", marginBottom: 2, color: "#fff" }}>
                            Resumo de Venda dos Anéis
                        </Typography>

                        <Box
                            sx={{
                                maxHeight: "210px",
                                overflowY: "auto",
                                pr: 1,
                                "&::-webkit-scrollbar": { width: "6px" },
                                "&::-webkit-scrollbar-thumb": {
                                    backgroundColor: colors.pink[500],
                                    borderRadius: "4px",
                                },
                                "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                            }}
                        >
                            {ringSalesData.map((item, index) => {
                                let percentage = 0;

                                if (!showPercentBase) {
                                    percentage = totalRingsSold > 0
                                        ? (item.sold / totalRingsSold) * 100
                                        : 0;
                                } else {
                                    percentage = item.total > 0
                                        ? (item.sold / item.total) * 100
                                        : 0;
                                }

                                return (
                                    <Box key={`${item.size}-${index}`} sx={{ marginBottom: 2 }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                marginBottom: 0.5,
                                                color: "#fff",
                                            }}
                                        >
                                            <Typography variant="body2">Tamanho {item.size}</Typography>
                                            <Typography variant="body2">
                                                {showPercentBase
                                                    ? `${item.sold}/${item.total}`
                                                    : `${item.sold}/${totalRingsSold}`}
                                            </Typography>
                                        </Box>

                                        <LinearProgress
                                            variant="determinate"
                                            value={percentage}
                                            sx={{
                                                height: 8,
                                                borderRadius: 5,
                                                backgroundColor: "rgba(255,255,255,0.3)",
                                                "& .MuiLinearProgress-bar": {
                                                    backgroundColor:
                                                        percentage >= 90
                                                            ? "#4caf50"
                                                            : percentage >= 30
                                                                ? "#ff9800"
                                                                : "#f44336",
                                                    transition: "width 0.5s ease-in-out",
                                                },
                                            }}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                </Box>
            </Box>

            <Paper elevation={6} sx={{ backgroundColor: colors.pink[800], padding: 2, width: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap", mb: 2 }}>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            Vendas por cor de banho
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.78 }}>
                            Comparativo entre o que foi enviado nas maletas e o que foi vendido de fato.
                        </Typography>
                    </Box>

                    <Chip
                        size="small"
                        label={scopeLabel}
                        sx={{
                            color: "#fff",
                            backgroundColor: "#ffffff18",
                            fontWeight: 600,
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "240px 1fr 360px",
                        },
                        gap: 2,
                        alignItems: "stretch",
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: "#ffffff0d",
                            borderRadius: 3,
                            padding: 2,
                            minHeight: 220,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {colorBathSalesData.length > 0 ? (
                            <PieChart
                                data={colorBathSalesData}
                                lineWidth={100}
                                paddingAngle={1.5}
                                rounded={false}
                                animate
                                label={({ dataEntry }) => {
                                    const percentage = totalColorBathSoldUnits > 0
                                        ? Math.round((dataEntry.value / totalColorBathSoldUnits) * 100)
                                        : 0;

                                    return percentage >= 8 ? `${percentage}%` : "";
                                }}
                                labelStyle={{
                                    fontSize: "7px",
                                    fill: "#fff",
                                    fontWeight: "bold",
                                }}
                                labelPosition={62}
                                style={{ height: "190px" }}
                            />
                        ) : (
                            <Typography variant="body2" sx={{ opacity: 0.78 }}>
                                Sem vendas por cor no período.
                            </Typography>
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                lg: "1fr 1fr",
                            },
                            gap: 1,
                            minWidth: 0,
                            alignContent: "start",
                        }}
                    >
                        {colorPerformanceData.map((item) => {
                            const sentDiff = item.soldPercentage - item.sentPercentage;

                            return (
                                <Box
                                    key={item.color}
                                    sx={{
                                        backgroundColor: "#ffffff11",
                                        borderRadius: 2,
                                        padding: "12px 14px",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 1.5,
                                            mb: 1,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: "50%",
                                                    backgroundColor: item.chartColor,
                                                    flexShrink: 0,
                                                }}
                                            />

                                            <Typography
                                                variant="body2"
                                                fontWeight="bold"
                                                sx={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {item.color}
                                            </Typography>
                                        </Box>

                                        <Chip
                                            size="small"
                                            label={`${item.conversionPercentage}% conversão`}
                                            sx={{
                                                color: "#fff",
                                                height: 22,
                                                backgroundColor:
                                                    item.conversionPercentage >= 70
                                                        ? "rgba(76, 175, 80, 0.35)"
                                                        : item.conversionPercentage >= 35
                                                            ? "rgba(255, 152, 0, 0.35)"
                                                            : "rgba(244, 67, 54, 0.35)",
                                                fontSize: "0.7rem",
                                                fontWeight: 700,
                                            }}
                                        />
                                    </Box>

                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.85 }}>
                                        <Box>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.35 }}>
                                                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                                                    Enviado na maleta
                                                </Typography>
                                                <Typography variant="caption" fontWeight="bold">
                                                    {item.sentPercentage}% · {formatNumber(item.quantitySent)} un.
                                                </Typography>
                                            </Box>

                                            <LinearProgress
                                                variant="determinate"
                                                value={item.sentPercentage}
                                                sx={{
                                                    height: 7,
                                                    borderRadius: 5,
                                                    backgroundColor: "rgba(255,255,255,0.18)",
                                                    "& .MuiLinearProgress-bar": {
                                                        backgroundColor: "rgba(255,255,255,0.55)",
                                                        borderRadius: 5,
                                                    },
                                                }}
                                            />
                                        </Box>

                                        <Box>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.35 }}>
                                                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                                                    Vendido de fato
                                                </Typography>
                                                <Typography variant="caption" fontWeight="bold">
                                                    {item.soldPercentage}% · {formatNumber(item.quantitySold)} un.
                                                </Typography>
                                            </Box>

                                            <LinearProgress
                                                variant="determinate"
                                                value={item.soldPercentage}
                                                sx={{
                                                    height: 8,
                                                    borderRadius: 5,
                                                    backgroundColor: "rgba(255,255,255,0.18)",
                                                    "& .MuiLinearProgress-bar": {
                                                        backgroundColor: item.chartColor,
                                                        borderRadius: 5,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 1,
                                            mt: 1,
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ opacity: 0.72 }}>
                                            Vendido: {formatCurrency(item.soldValue)}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 700,
                                                color:
                                                    sentDiff > 0
                                                        ? "#8EF0A6"
                                                        : sentDiff < 0
                                                            ? "#FFB4A8"
                                                            : "rgba(255,255,255,0.72)",
                                            }}
                                        >
                                            {sentDiff > 0 ? "+" : ""}
                                            {sentDiff} pts vs envio
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 1,
                        }}
                    >
                        <Box sx={{ backgroundColor: "#ffffff11", borderRadius: 2, padding: 1.5, minHeight: 92 }}>
                            <Typography variant="caption" sx={{ opacity: 0.72 }}>
                                Produtos vendidos
                            </Typography>
                            <Typography variant="h5" fontWeight="bold">
                                {formatNumber(totalColorBathSoldUnits)}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.72 }}>
                                Unidades vendidas
                            </Typography>
                        </Box>

                        <Box sx={{ backgroundColor: "#ffffff11", borderRadius: 2, padding: 1.5, minHeight: 92 }}>
                            <Typography variant="caption" sx={{ opacity: 0.72 }}>
                                Valor vendido
                            </Typography>
                            <Typography variant="h5" fontWeight="bold">
                                {formatCurrency(totalColorBathSoldValue)}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.72 }}>
                                Total realizado
                            </Typography>
                        </Box>

                        <Box sx={{ backgroundColor: "#ffffff11", borderRadius: 2, padding: 1.5, minHeight: 92 }}>
                            <Typography variant="caption" sx={{ opacity: 0.72 }}>
                                Ticket médio vendido
                            </Typography>
                            <Typography variant="h5" fontWeight="bold">
                                {formatCurrency(averageSoldProductTicket)}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.72 }}>
                                Produtos vendidos
                            </Typography>
                        </Box>

                        <Box sx={{ backgroundColor: "#ffffff11", borderRadius: 2, padding: 1.5, minHeight: 92 }}>
                            <Typography variant="caption" sx={{ opacity: 0.72 }}>
                                Ticket médio na maleta
                            </Typography>
                            <Typography variant="h5" fontWeight="bold">
                                {formatCurrency(averageProductsInsideKitTicket)}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.72 }}>
                                {formatNumber(totalProductsInsideKits)} un. analisadas
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default ResumeInfoBox;